/** English (default) translations */
export const en = {
  // Brand / hero
  "brand.tagline":          "Professional shapefile tools · browser only",
  "welcome.eyebrow":        "Welcome",
  "welcome.title.line1":    "Every shapefile tool,",
  "welcome.title.line2":    "in one browser tab.",
  "welcome.subtitle":       "Reproject, convert, validate, buffer, simplify, measure, merge, clip and convert coordinates right in your browser. Your data never leaves your device.",
  "welcome.hint":           "Pick a tool from the donut wheel to begin",
  "welcome.card.fast":      "Lightning Fast",
  "welcome.card.fast.desc": "All work happens locally in your browser.",
  "welcome.card.private":   "100% Private",
  "welcome.card.private.desc": "Files never leave your device. Zero uploads.",
  "welcome.card.offline":   "Works Offline",
  "welcome.card.offline.desc": "Once loaded, every tool runs without internet.",
  "welcome.card.free":      "Always Free",
  "welcome.card.free.desc": "Professional GIS tools at zero cost.",

  // Tool labels (donut)
  "tool.reproject":   "Reproject",
  "tool.converter":   "Convert",
  "tool.validate":    "Validate",
  "tool.buffer":      "Buffer",
  "tool.simplify":    "Simplify",
  "tool.calculate":   "Calculate",
  "tool.merge":       "Merge",
  "tool.clip":        "Clip",
  "tool.coordinate":  "Coordinates",
  "tool.layout":      "Layout",
  "tool.visualize":   "Share",
  "tool.share":       "Share",

  // Bottom nav
  "nav.faq":      "FAQ",
  "nav.about":    "About",
  "nav.feedback": "Feedback",
  "nav.blog":     "Blog",
  "nav.roadmap":     "Roadmap",
  "nav.roadmapHint": "See what's planned & shipped",
  "nav.help":        "Help the developer",
  "nav.helpHint":    "Send feedback & see recent updates",
  "help.title":      "Help the developer",
  "help.subtitle":   "Send feedback or request a feature, and see what shipped recently.",
  "help.reqTitle":   "You asked, we build",
  "help.reqNote":    "Real requests from users like you. Send one below, it lands on this list and gets worked on.",
  "sheet.faqTitle":   "Frequently Asked Questions",
  "sheet.faqDesc":    "Quick answers about how Shp.Aid works, supported formats and privacy.",
  "sheet.aboutTitle": "About Shp.Aid",
  "sheet.aboutDesc":  "Tech stack, features and developer credits.",
  "nav.blogHint":     "GIS guides & tutorials",

  // Donut
  "donut.reset":  "tap to reset",
  "donut.pick":   "pick a tool",

  // File uploader
  "upload.drop":     "Drop files here or click to browse",
  "upload.supports": "Supports: {formats}",
  "upload.maxsize":  "Max file size: {size}",
  "upload.selected": "Selected files",
  "upload.remove":   "Remove file",

  // Tool panel
  "panel.tool":         "Tool Panel",
  "panel.map":          "Map",
  "panel.map.show":     "Show live map preview",
  "panel.map.hide":     "Hide map preview",
  "panel.map.pending":  "Run the tool to preview the result on a map",

  // Theme
  "theme.toggle":   "Toggle theme",
  "theme.to.dark":  "Switch to dark mode",
  "theme.to.light": "Switch to light mode",

  // Language
  "lang.toggle":  "Language",
  "lang.en":      "English",
  "lang.id":      "Bahasa Indonesia",

  // Tool intros (the info box at the top of each tool) + I/O labels
  "io.in":  "Input",
  "io.out": "Output",
  "io.shp": "Shapefile (.zip, or .shp + .dbf + .prj)",

  "intro.reproject":      "Transform a shapefile's coordinates from one projection (EPSG) to another. The source projection is auto-detected from the .prj when present.",
  "intro.reproject.out":  "Shapefile ZIP (re-projected)",
  "intro.converter":      "Convert geospatial data between formats. Pick a From and To format, upload, then download. CSV must contain lon/lat columns.",
  "intro.converter.in":   "Shapefile, GeoJSON, CSV, KML/KMZ or GeoParquet",
  "intro.converter.out":  "Shapefile, GeoJSON, KML, CSV, GPX or DXF",
  "intro.validate":       "“Validate” checks geometry for errors (self-intersections, unclosed rings, duplicate points). “Fix” auto-repairs them and removes duplicate features.",
  "intro.validate.out":   "Fixed Shapefile ZIP",
  "intro.buffer":         "Create a buffer zone around each feature at a set distance. Use a positive distance to grow, or a negative distance to shrink (inward buffer).",
  "intro.buffer.in":      "Shapefile or GeoJSON",
  "intro.buffer.out":     "Shapefile ZIP (buffer polygons)",
  "intro.simplify":       "Reduce the number of vertices in lines and polygons (Douglas–Peucker) while keeping the overall shape. Higher tolerance = simpler geometry.",
  "intro.simplify.out":   "Shapefile ZIP (fewer vertices)",
  "intro.calculate":      "Measure area & perimeter (polygons) and length (lines) per feature. Results can be written back as attributes or exported to CSV.",
  "intro.calculate.out":  "Shapefile ZIP (with fields) or CSV",
  "intro.merge":          "Combine several shapefiles into one. Each feature is tagged with its source file. Mixing point/line/polygon types is flagged as incompatible.",
  "intro.merge.in":       "Multiple Shapefiles (same geometry type)",
  "intro.merge.out":      "Shapefile ZIP (combined)",
  "intro.clip":           "Crop features to a rectangular extent (bounding box). Enter bounds as minX,minY,maxX,maxY or reduce the current extent.",
  "intro.clip.out":       "Shapefile ZIP (clipped)",
  "intro.visualize":      "Share GIS layers via end-to-end AES-256 encrypted links directly in your browser with no backend server.",
  "intro.share":          "Share GIS layers via end-to-end AES-256 encrypted links directly in your browser with no backend server.",
  "io.url":               "Encrypted share link (AES-256)",
  "cv.batchTitle":        "Batch: {n} files → 1 ZIP",
  "cv.batchMax":          "max {n} files",
  "cv.batchNote":         "Each file is converted separately, then bundled into one ZIP. A file that fails does not cancel the others.",
  "cv.batchReady":        "{n} files ready to convert",
  "cv.batchReadyDesc":    "Press Convert to download the results as a single ZIP.",
  "cv.batchProgress":     "{done}/{total} — {name}",
  "cv.batchDone":         "{n} files converted, saved as {name}",
  "cv.batchPartial":      "{ok} files converted, {fail} failed — see the list in the panel.",
  "csv.mapping":          "Coordinate columns",
  "csv.x":                "X column (Longitude / Easting)",
  "csv.y":                "Y column (Latitude / Northing)",
  "csv.crs":              "Source coordinate system",
  "csv.delimiter":        "Delimiter",
  "csv.decimal":          "Decimal",
  "csv.preview":          "Sample value",
  "csv.pickCols":         "Pick the X and Y columns to continue.",
  "csv.reprojected":      "will be reprojected to WGS84",
  "csv.rowsSkipped":      "{n} rows skipped (missing or invalid coordinates).",
  "intro.layout":         "Compose a print-ready map: pick a paper size (A4/A3/Letter) and orientation, set the scale, then export to PDF complete with title, north arrow, scale bar and legend.",
  "intro.layout.in":      "Layers in the Map Studio (SHP/GeoJSON)",
  "intro.layout.out":     "Print-ready PDF map layout",

  // Common tool actions / status
  "act.reproject":   "Reproject Shapefile",
  "act.convert":     "Convert & Download",
  "act.buffer":      "Create Buffer",
  "act.simplify":    "Simplify Geometry",
  "act.calculate":   "Calculate Measurements",
  "act.recalculate": "Recalculate",
  "act.merge":       "Merge Shapefiles",
  "act.clip":        "Clip by Extent",
  "act.download":    "Download Result",
  "act.downloadShp": "Download Shapefile",
  "act.exportCsv":   "Export CSV",
  "act.validate":    "Validate",
  "act.fix":         "Fix Geometry",
  "act.reading":    "Reading…",
  "act.processing": "Processing…",
  "status.reading":      "Reading file…",
  "status.processing":   "Processing…",
  "status.complete":     "Done!",

  // Shared tool labels / toasts
  "lbl.uploadShp":   "Upload Shapefile",
  "lbl.units":       "Units",
  "lbl.inputFormat": "Input Format",
  "lbl.from":        "From",
  "lbl.to":          "To",
  "proj.popular":    "Popular Projections",
  "proj.other":      "Other Projections",
  "proj.selectSrc":  "Select source EPSG",
  "proj.targetEpsg": "Target EPSG Code",
  "unit.kilometers": "Kilometers",
  "unit.meters":     "Meters",
  "unit.miles":      "Miles",
  "unit.feet":       "Feet",
  "unit.hectares":   "Hectares",
  "unit.acres":      "Acres",
  "toast.fileLoaded":   "File loaded",
  "toast.featuresN":    "{n} features found",
  "toast.pointsN":      "{n} points found",
  "toast.error":        "Error",
  "toast.noData":       "No data",
  "toast.noDataDesc":   "Please upload a file first",
  "toast.rateLimit":    "Rate limit exceeded",
  "toast.dlComplete":   "Download complete",
  "toast.savedAs":      "Saved as {name}",
  "toast.dlFailed":     "Download failed",
  "toast.uploadError":  "Upload error",

  // Reproject
  "rp.step1":        "1. Upload Shapefile",
  "rp.step2":        "2. Source Projection",
  "rp.step3":        "3. Target Projection",
  "rp.detected":     "Detected: {info}",
  "rp.projDetected": "Projection detected",
  "rp.projSrc":      "Source: EPSG:{code}",
  "rp.projUnknown":  "Projection unknown",
  "rp.projUnknownD": "Please select source projection manually",
  "rp.missingInfo":  "Missing information",
  "rp.missingInfoD": "Please select both source and target projections",
  "rp.sameProj":     "Same projection",
  "rp.sameProjD":    "Source and target projections are the same",
  "rp.complete":     "Reprojection complete",
  "rp.completeD":    "Converted from EPSG:{src} to EPSG:{tgt}",
  "rp.errTitle":     "Reprojection error",
  "rp.success":      "Reprojection complete! Your data was converted from EPSG:{src} to EPSG:{tgt}. Click Download to save the result.",
  "rp.errNoPrj":     "Missing .prj file, cannot detect projection. Please select the source projection manually.",
  "rp.errNoDbf":     "Missing .dbf file, attribute table required for a shapefile.",
  "rp.errFormat":    "Invalid file format. Expected: .shp + .dbf + .prj files or a .zip bundle.",
  "rp.errRead":      "Failed to read shapefile",
  "rp.errFailed":    "Reprojection failed",

  // Buffer
  "bf.distance":      "Buffer Distance",
  "bf.invalidDist":   "Invalid distance",
  "bf.enterNumber":   "Please enter a valid number",
  "bf.complete":      "Buffer complete",
  "bf.completeD":     "Created buffers for {n} features",
  "bf.failed":        "Buffer failed",
  "bf.statusProc":    "Creating buffers…",
  "bf.statusDone":    "Buffer complete!",

  // Simplify
  "sm.tolerance":     "Tolerance",
  "sm.level":         "Simplification level",
  "sm.low":           "Low",
  "sm.medium":        "Medium",
  "sm.high":          "High",
  "sm.highQuality":   "High quality (slower, more accurate)",
  "sm.complete":      "Simplification complete",
  "sm.completeD":     "Reduced vertices by {pct}%",
  "sm.failed":        "Simplification failed",
  "sm.statusProc":    "Simplifying…",
  "sm.statusDone":    "Simplification complete!",
  "sm.reduction":     "Vertex reduction",
  "sm.toleranceHint": "Higher tolerance = more simplification",
  "sm.projHint":      " (projected CRS, values in meters)",
  "sm.hqMode":        "High Quality Mode",
  "sm.statusDoneD":   "Simplified! Reduced vertices by {pct}%",

  // Calculate
  "cl.measurements":  "Measurements to calculate",
  "cl.area":          "Area (for polygons)",
  "cl.length":        "Length (for lines)",
  "cl.perimeter":     "Perimeter (for polygons)",
  "cl.complete":      "Calculation complete",
  "cl.completeD":     "Calculated measurements for {n} features",
  "cl.failed":        "Calculation failed",
  "cl.statusProc":    "Calculating measurements…",
  "cl.statusDone":    "Calculation complete!",
  "cl.summary":       "Summary",
  "cl.totalFeatures": "Total features",
  "cl.totalArea":     "Total area",
  "cl.totalLength":   "Total length",
  "cl.totalPerim":    "Total perimeter",
  "cl.geomTypes":     "Geometry types",
  "cl.featureDetails":"Feature details",
  "cl.csvExported":   "CSV exported",
  "cl.cFeature":      "Feature",
  "cl.cType":         "Type",
  "cl.cArea":         "Area",
  "cl.cLength":       "Length",
  "cl.cPerimeter":    "Perimeter",
  "cl.showing":       "Showing first 100 of {n} features",

  // Merge
  "mg.uploadMulti":   "Upload Shapefiles to Merge",
  "mg.filesToMerge":  "Files to merge ({n})",
  "mg.clearAll":      "Clear all",
  "mg.needTwo":       "Add at least 2 files to merge",
  "mg.complete":      "Merge complete",
  "mg.completeD":     "Merged {files} files into {feats} features",
  "mg.failed":        "Merge failed",
  "mg.statusProc":    "Merging files…",
  "mg.statusDone":    "Merge complete!",
  "mg.results":       "Merge results",
  "mg.warning":       "Warning",
  "mg.filesAdded":    "Added {n} file(s)",
  "mg.filesLoaded":   "Files loaded",
  "mg.notEnough":     "Not enough files",
  "mg.crsError":      "CRS error",
  "mg.crsWarning":    "CRS warning",
  "mg.clickUpload":   "Click to upload multiple Shapefiles",
  "mg.uploadHint":    "Select multiple .zip files or individual Shapefile components",
  "mg.compatOk":      "✓ Files are compatible for merging",
  "mg.sourceFiles":   "Source files",
  "lbl.features":     "{n} features",

  // Clip
  "cp.method":        "Clip method",
  "cp.bbox":          "Bounding box",
  "cp.current":       "Reduce extent",
  "cp.bounds":        "Bounds (minX, minY, maxX, maxY)",
  "cp.currentExtent": "Current extent",
  "cp.noFeatures":    "No features in extent",
  "cp.noFeaturesD":   "The clipping extent doesn't intersect any features",
  "cp.complete":      "Clip complete",
  "cp.completeD":     "Clipped to {n} features (from {orig})",
  "cp.failed":        "Clip failed",
  "cp.statusProc":    "Clipping features…",
  "cp.statusDone":    "Clipped to {n} features!",
  "cp.results":       "Clip results",
  "cp.clipped":       "Clipped",
  "cp.retention":     "Retention rate: {pct}%",
  "cp.invalidBbox":   "Invalid bounding box",
  "cp.bboxFormat":    "Please enter bbox as: minX, minY, maxX, maxY",
  "cp.noExtent":      "Could not determine current extent",
  "cp.useCurrent":    "Use current extent",
  "cp.reduceHint":    "Clips to the inner 50% of the data extent, shrinking each edge by 25%.",
  "cp.result":        "Result: {bbox}",
  "cp.currentBboxLabel": "Current extent (BBox):",
  "cp.formatHint":    "Format: minX, minY, maxX, maxY",
  "cp.tabBbox":       "Custom BBox",
  "cp.tabReduce":     "Reduce extent 50%",
  "cp.enterCoords":   "Enter coordinates separated by commas",
  "cp.original":      "Original",
  "cp.removed":       "Removed",
  "cp.tabShapefile":  "By shapefile",
  "cp.maskUpload":    "Mask shapefile (polygon)",
  "cp.maskHint":      "Upload a polygon shapefile to use as the clip boundary. It must match the source CRS and overlap its extent.",
  "cp.maskCurrent":   "Mask: {name}",
  "cp.maskLoaded":    "Mask loaded",
  "cp.maskMissing":   "No mask",
  "cp.maskMissingD":  "Upload a polygon shapefile to clip by",
  "cp.maskNeedSource":  "Upload the source first",
  "cp.maskNeedSourceD": "Upload the shapefile to clip before the mask",
  "cp.maskNotPolygon":  "Mask must be polygon",
  "cp.maskNotPolygonD": "The mask shapefile must contain Polygon/MultiPolygon geometry",
  "cp.maskCrsMismatch": "CRS mismatch",
  "cp.maskCrsMismatchD":"Source is EPSG:{src} but mask is EPSG:{mask}. Reproject one to match first.",
  "cp.maskNoOverlap":   "No overlap",
  "cp.maskNoOverlapD":  "The mask extent doesn't overlap the source, there would be nothing to clip.",

  // Validate & Fix
  "vl.tabValidate":   "Validate",
  "vl.tabFix":        "Fix errors",
  "vl.complete":      "Validation complete",
  "vl.fixComplete":   "Fix complete",
  "vl.fixCompleteD":  "Fixed {fixed} features, removed {removed} unfixable features",
  "vl.failed":        "Validation failed",
  "vl.fixFailed":     "Fix failed",
  "vl.statusValidating": "Validating…",
  "vl.statusFixing":     "Fixing geometries…",
  "vl.statusValidDone":  "Validation complete!",
  "vl.statusFixDone":    "Fix complete!",
  "vl.valid":         "Valid",
  "vl.invalid":       "Invalid",
  "vl.errors":        "Errors",
  "vl.warnings":      "Warnings",
  "vl.fixResults":    "Fix results",
  "vl.fixedFeatures": "Fixed features",
  "vl.fixLog":        "Fix log",
  "vl.allValid":      "All geometries are valid!",
  "vl.foundInvalid":  "Found {n} invalid features",
  "vl.totalFeatures": "Total features",
  "vl.removed":       "Removed",
  "vl.validateGeom":  "Validate geometry, then fix any problems found.",
  "vl.toolMode":      "Tool mode",
  "vl.results":       "Validation results",
  "vl.validFeatures": "Valid features",
  "vl.invalidFeatures":"Invalid features",
  "vl.status":        "Status",
  "vl.removedFeatures":"Removed features",
  "vl.statusValid":   "✓ Valid",
  "vl.statusInvalid": "✗ Invalid",
  "vl.errorsLabel":   "Errors:",
  "vl.fixLogLabel":   "Fix log:",

  // Errors
  // Converter
  "cv.geojsonLoaded":   "GeoJSON loaded",
  "cv.csvLoaded":       "CSV loaded",
  "cv.shpLoaded":       "Shapefile loaded",
  "cv.kmlLoaded":       "KML/KMZ loaded",
  "cv.parquetLoaded":   "GeoParquet loaded",
  "cv.complete":        "Conversion complete",
  "cv.failed":          "Conversion failed",
  "cv.uploadFmt":       "Upload {fmt}",
  "cv.statusReading":   "Reading file…",
  "cv.statusConverting":"Converting…",
  "cv.statusDone":      "Conversion complete!",

  // Coordinate converter
  "cd.datum":      "All conversions use the WGS 84 datum (EPSG:4326)",
  "cd.mapView":    "View on map",
  "cd.mapHint":    "Show this coordinate on an interactive map",
  "cd.dd":         "Decimal Degrees (DD)",
  "cd.dms":        "Degrees Minutes Seconds (DMS)",
  "cd.utm":        "Universal Transverse Mercator (UTM)",
  "cd.lat":        "Latitude",
  "cd.lon":        "Longitude",
  "cd.zone":       "Zone",
  "cd.easting":    "Easting (m)",
  "cd.northing":   "Northing (m)",
  "cd.zoneInput":  "Zone (e.g. 49S)",
  "cd.convert":    "Convert",
  "cd.copy":       "Copy",
  "cd.copied":     "Copied",
  "cd.zoneRef":    "Indonesia UTM Zone Reference",
  "cd.colLonRange":"Longitude range",
  "cd.colProvinces":"Provinces / Regions",
  "cd.accuracy":   "Accuracy Notes",
  "cd.note1":      "Datum: WGS 84, the standard for GPS and global mapping (EPSG:4326).",
  "cd.note2":      "DD precision: 6 decimals ≈ 0.11 m at the equator.",
  "cd.note3":      "DMS precision: 4 decimal seconds ≈ 0.03 m at the equator.",
  "cd.note4":      "UTM precision: 3 decimals (mm), adequate for surveying.",
  "cd.note5":      "UTM is valid within each 6° zone; distortion grows toward zone edges.",
  "cd.note6":      "No geoid (EGM96/EGM2008) or atmospheric corrections are applied.",
  "cd.hintLat":    "Decimal degrees, −90 to 90. Use a dot for decimals; minus (−) for South. e.g. -6.200000",
  "cd.hintLon":    "Decimal degrees, −180 to 180. Use a dot for decimals; minus (−) for West. e.g. 106.816666",
  "cd.hintDegLat": "Whole degrees, 0 to 90",
  "cd.hintDegLon": "Whole degrees, 0 to 180",
  "cd.hintMin":    "Minutes, 0 to 59",
  "cd.hintSec":    "Seconds, 0 to 59.9999. Use a dot for decimals.",
  "cd.hintZone":   "Zone number 1–60 + hemisphere letter N or S. e.g. 49S",
  "cd.hintEasting":"Easting in metres, 100,000–900,000. Use a dot for decimals. e.g. 695143.214",
  "cd.hintNorthing":"Northing in metres. Use a dot for decimals. e.g. 9314278.623",
  "cd.errDD":      "Enter valid latitude and longitude values.",
  "cd.errLat":     "Latitude must be between -90° and 90°.",
  "cd.errLon":     "Longitude must be between -180° and 180°.",
  "cd.errDMSNeg":    "Degrees, minutes and seconds must be positive numbers — the N/S/E/W selector sets the hemisphere.",
  "cd.errDMS":     "Fill all DMS fields with valid numbers.",
  "cd.errDMSLat":  "Invalid DMS latitude (D: 0-90, M: 0-59, S: 0-59.9999).",
  "cd.errDMSLon":  "Invalid DMS longitude (D: 0-180, M: 0-59, S: 0-59.9999).",
  "cd.errZone":    "Enter a UTM zone (e.g. 49S).",
  "cd.errEN":      "Enter valid easting and northing values (metres).",
  "cd.errEast":    "Easting should be between 100,000 m and 900,000 m.",
  "cd.errOutRange":"Resulting coordinates are out of valid geographic range. Check zone / values.",
  "cd.errUtm":     "UTM conversion failed.",

  // FAQ
  "faq.title": "Frequently Asked Questions",
  "faq.q1": "What is a Shapefile?",
  "faq.a1": "A Shapefile is a geospatial vector data format developed by Esri. It's widely used for storing geographic features like points, lines, and polygons, along with their attributes.",
  "faq.q2": "Is my data secure?",
  "faq.a2": "Absolutely. All processing happens entirely in your browser using JavaScript. Your files never leave your computer and are not uploaded to any server.",
  "faq.q3": "What file size limits exist?",
  "faq.a3": "Since processing happens in your browser, the limit depends on your device's memory. Most modern computers can handle files up to several hundred megabytes without issues.",
  "faq.q4": "Do I need to install anything?",
  "faq.a4": "No installation required! Shp.Aid works entirely in your web browser. Just visit the site and start using the tools immediately.",
  "faq.q5": "Which browsers are supported?",
  "faq.a5": "Shp.Aid works best on modern browsers like Chrome, Firefox, Safari, and Edge. We recommend using the latest version for optimal performance.",

  // About / Features
  "ft.fast": "Lightning Fast",
  "ft.fastD": "All processing happens locally in your browser using WebAssembly-level JavaScript libraries. No server round-trips, no upload wait time.",
  "ft.private": "100% Private",
  "ft.privateD": "Your files never leave your device. Zero telemetry, zero uploads. Your spatial data stays yours.",
  "ft.offline": "Works Offline",
  "ft.offlineD": "Once the page has loaded, every tool functions without an internet connection. Ideal for field work and air-gapped environments.",
  "ft.free": "Always Free",
  "ft.freeD": "No subscriptions, no hidden fees, no paywalls. Professional-grade shapefile tools at zero cost, forever.",
  "ft.proj": "Proj4 Precision",
  "ft.projD": "Coordinate reprojection and UTM conversion powered by proj4js, the same projection engine used in QGIS and GDAL pipelines.",
  "ft.format": "Multi-Format Output",
  "ft.formatD": "Export results as Shapefile (zipped), GeoJSON, KML, CSV, or TopoJSON directly from your browser with a single click.",
  "ft.preview": "Live Map Preview",
  "ft.previewD": "Spatial tools include an inline Leaflet map preview so you can visually verify buffer zones, clip extents, and geometry results.",
  "ft.tools": "9 Integrated Tools",
  "ft.toolsD": "Reproject · Convert · Validate & Fix · Buffer · Simplify · Measure · Merge · Clip · Coordinate Converter, all in one seamless interface.",
  "ft.techStack": "Technology Stack",
  "ft.runtime": "Runtime",
  "ft.runtimeV": "React 18 + TypeScript, fully client-side, zero backend",
  "ft.geospatial": "Geospatial",
  "ft.mapPreview": "Map preview",
  "ft.mapPreviewV": "React-Leaflet v4 with OpenStreetMap tiles",
  "ft.bundler": "Bundler",
  "ft.bundlerV": "Vite 5 with SWC, fast builds, instant HMR",
  "ft.styling": "Styling",
  "ft.stylingV": "Tailwind CSS + Radix UI primitives + custom design tokens",
  "ft.devBy": "Developed by",
  "ft.license": "License",
  "ft.licenseVal": "MIT · Free & Open Source",
  "ft.footer": "Shp.Aid is a browser-only tool. Data processed locally, no server contact required.",

  // Feedback
  "fb.title":        "Send Feedback",
  "fb.subtitle":     "We'd love to hear your thoughts about Shp.Aid",
  "fb.name":         "Name",
  "fb.namePh":       "Your name",
  "fb.email":        "Email",
  "fb.rating":       "Rating",
  "fb.message":      "Message",
  "fb.messagePh":    "Tell us what you think...",
  "fb.missing":      "Missing fields",
  "fb.missingD":     "Please fill in all required fields",
  "fb.invalidEmail": "Invalid email",
  "fb.invalidEmailD":"Please enter a valid email address",
  "fb.tooMany":      "Too many submissions",
  "fb.sent":         "Feedback sent!",
  "fb.sentD":        "Thank you for your feedback. We'll review it soon.",
  "fb.failed":       "Failed to send",
  "fb.failedD":      "Please try again or contact us directly",
  "fb.thankYou":     "Thank you for your feedback!",
  "fb.reviewSoon":   "We'll review it soon.",
  "fb.cancel":       "Cancel",
  "fb.send":         "Send Feedback",
  "fb.sending":      "Sending…",
  "fb.stars":        "{n} star{s}",

  // Legal page
  "lg.back":       "Back to Shp.Aid",
  "lg.title":      "Legal",
  "lg.subtitle":   "Privacy, terms and open-source credits for Shp.Aid.",
  "lg.navPrivacy": "Privacy Policy",
  "lg.navTerms":   "Terms of Use",
  "lg.navDisclaimer": "Accuracy Disclaimer",
  "lg.navAttr":    "Open Source Attributions",
  "lg.effective":  "Effective: 24 May 2026",
  "lg.pShortB":    "Short version:",
  "lg.pShort":     "Shp.Aid does not collect, store, transmit or share your data. Every file you load is processed entirely in your browser. Your files never reach our servers, because there are no servers handling your files.",
  "lg.pCollectH":  "What data we collect",
  "lg.pCollect1B": "None of your files.",
  "lg.pCollect1":  "All shapefile, GeoJSON, KML, CSV, GPX and DXF processing happens locally in your browser using JavaScript libraries (Turf.js, proj4js, shpjs). The original files and the converted outputs never leave your device.",
  "lg.pCollect2":  "If we add anonymous usage analytics (e.g. Plausible) in the future, it will track only aggregate metrics: which tool was clicked, how often the site is visited, and what country the visit came from. It will NEVER track file contents, file names, or any user-identifying information.",
  "lg.pThirdH":    "Third-party services we contact",
  "lg.pThird1":    "OpenStreetMap tiles, only when you open the live map preview. Your browser requests map tiles directly from openstreetmap.org. We do not proxy or log these requests.",
  "lg.pThird2":    "Map marker icons are bundled with the app and served from this site. No third-party icon CDN is used.",
  "lg.pCookieH":   "Cookies & local storage",
  "lg.pCookie":    "Shp.Aid does not set tracking cookies. It uses browser localStorage only to remember your preferences (theme, language, last-used options). You can clear this at any time from your browser settings.",
  "lg.pRightsH":   "Your rights (GDPR / CCPA)",
  "lg.pRights":    "Since we hold no personal data, there is nothing to delete, export or correct. If you have questions, contact the maintainer via the Feedback button on the main page.",
  "lg.tIntro":     "By using Shp.Aid you agree to these terms.",
  "lg.t1H":        "1. Service description",
  "lg.t1":         "Shp.Aid is a free, open-source, browser-only set of tools for working with geographic data (shapefile conversion, reprojection, buffering, simplification, measurement, merging, clipping, coordinate conversion). It is provided \"as is\" with no service-level commitments.",
  "lg.t2H":        "2. License",
  "lg.t2":         "The Shp.Aid source code is released under the MIT License. You may use, copy, modify, and redistribute it freely. See the LICENSE file in the repository for the full text.",
  "lg.t3H":        "3. Acceptable use",
  "lg.t3a":        "Do not use Shp.Aid for illegal purposes or to process data you don't have the right to process.",
  "lg.t3b":        "Do not attempt to attack, overload or reverse-engineer the hosted version of the app.",
  "lg.t3c":        "Do not redistribute the live hosted app under another brand without attribution.",
  "lg.t4H":        "4. No warranty & limitation of liability",
  "lg.t4":         "SHP.AID IS PROVIDED \"AS IS\", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. To the maximum extent permitted by law, the authors and contributors shall not be liable for any damages arising from the use of this software, including data loss, incorrect coordinates, or any consequential damage from decisions made on the basis of its output.",
  "lg.dLead":      "Shp.Aid is a productivity tool, not a certified surveying instrument.",
  "lg.dBody":      "Coordinate transformations use the WGS 84 datum (EPSG:4326) and proj4js, the same projection engine used by QGIS and GDAL. In our test suite, the round-trip error is on the order of 10⁻¹⁴ degrees (machine epsilon) and polygon-area accuracy is within 0.02 % of independent reference values. However:",
  "lg.d1":         "No geoid correction (EGM96 / EGM2008) is applied. For high-precision elevation work, use a dedicated geodetic tool.",
  "lg.d2":         "UTM accuracy degrades near zone boundaries. If your data spans multiple UTM zones, consider reprojecting to a single zone before precision work.",
  "lg.d3":         "For cadastral, engineering, navigation or any safety-critical application, results must be independently verified using certified surveying software and procedures.",
  "lg.dEnd":       "The authors disclaim all responsibility for decisions made on the basis of Shp.Aid output without independent verification.",
  "lg.aIntro":     "Shp.Aid is built on the shoulders of open-source giants. Thank you to:",
  "lg.footer":     "Shp.Aid · Developed by Jokopal · MIT License",

  // 404
  "nf.notfound": "Page not found",
  "nf.home":     "Return to Home",

  // Errors
  "error.title":     "Something went wrong",
  "error.tool":      "This tool crashed. You can try again or pick another tool.",
  "error.chunk":     "We couldn't load that part of the app. Please refresh.",
  "error.retry":     "Try again",
  "error.reload":    "Reload",
} as const;
